import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Card,
  CardContent,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Avatar,
  Chip,
  LinearProgress,
  Paper,
  Button,
  Divider,
  IconButton,
} from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import InventoryIcon from '@mui/icons-material/Inventory';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import PeopleIcon from '@mui/icons-material/People';
import ReceiptIcon from '@mui/icons-material/Receipt';
import BusinessIcon from '@mui/icons-material/Business';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import TimelineIcon from '@mui/icons-material/Timeline';
import BarChartIcon from '@mui/icons-material/BarChart';
import PieChartIcon from '@mui/icons-material/PieChart';
import SpeedIcon from '@mui/icons-material/Speed';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import VisibilityIcon from '@mui/icons-material/Visibility';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import AssessmentIcon from '@mui/icons-material/Assessment';
import backendApi from '../shared/api/backendApi';

function Dashboard() {
  const [metrics, setMetrics] = useState({
    totalSales: 0,
    totalPurchases: 0,
    totalExpenses: 0,
    totalCogs: 0,
    inventoryValue: 0,
    totalProfit: 0,
    pendingPayments: 0,
    activeCustomers: 0,
    inventoryItems: 0,
    // Monthly comparison data
    currentMonthSales: 0,
    previousMonthSales: 0,
    salesGrowth: 0,
    currentMonthPurchases: 0,
    previousMonthPurchases: 0,
    purchasesGrowth: 0,
    currentMonthExpenses: 0,
    previousMonthExpenses: 0,
    currentMonthCogs: 0,
    previousMonthCogs: 0,
    currentMonthProfit: 0,
    previousMonthProfit: 0,
    profitGrowth: 0,
    currentMonthInventoryValue: 0,
    previousMonthInventoryValue: 0,
    inventoryGrowth: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [monthlyTarget, setMonthlyTarget] = useState(100000); // Default ₹1,00,000
  const [businessInsights, setBusinessInsights] = useState({
    lowStockItems: [],
    reorderSuggestions: [],
    alerts: [],
    personalizedInsights: {
      trendingProducts: [],
      topCustomers: [],
      demandPatterns: [],
      proactiveReorderSuggestions: [],
      productCombinations: { combinations: [], pairs: [], insights: [] },
      priceOptimization: [],
      bundleRecommendations: [],
      crossSellingOpportunities: [],
      dealerAnalysis: [],
      lowStockAlerts: []
    },
    comprehensiveInsights: {
      businessHealth: {
        status: 'NOT_LOADED',
        score: null,
        factors: {},
        trends: {},
        alerts: []
      },
      strategicRecommendations: [],
      riskAssessments: [],
      growthOpportunities: [],
      keyPerformanceIndicators: {}
    }
  });

  useEffect(() => {
    loadDashboardData();
    loadBusinessInsights();

    // Load monthly target from localStorage
    const savedTarget = localStorage.getItem('monthlyTarget');
    if (savedTarget) {
      setMonthlyTarget(parseFloat(savedTarget));
    }
  }, []);

  const loadBusinessInsights = async () => {
    try {
      // Get cached analytics data from localStorage (same as used in Business Intelligence)
      const cachedAnalyticsData = localStorage.getItem('businessAnalyticsData');
      const cachedAnalyticsTimestamp = localStorage.getItem('businessAnalyticsTimestamp');

      if (cachedAnalyticsData && cachedAnalyticsTimestamp) {
        const cacheAge = Date.now() - parseInt(cachedAnalyticsTimestamp);
        const cacheValidHours = 24; // Cache valid for 24 hours (same as BI page)

        if (cacheAge < (cacheValidHours * 60 * 60 * 1000)) {
          const analytics = JSON.parse(cachedAnalyticsData);

          // Extract data from the correct structure - backend sends data in overview.overview format
          const overviewData = analytics.overview?.overview || analytics.overview || {};
          const crossSheetInsights = overviewData.crossSheetInsights || {};
          const personalizedInsights = crossSheetInsights.personalizedInsights || {};

          // Extract comprehensive business insights
          const comprehensiveInsights = overviewData.comprehensiveInsights || {
            businessHealth: { status: 'UNKNOWN', score: 0 },
            strategicRecommendations: [],
            riskAssessments: [],
            growthOpportunities: [],
            keyPerformanceIndicators: {}
          };

          // Transform the data to match expected format
          const transformedData = {
            businessName: overviewData.businessName || 'Business',
            sheetId: overviewData.sheetId || '',
            generatedAt: overviewData.generatedAt || new Date().toISOString(),
            comprehensiveInsights,
            crossSheetInsights: crossSheetInsights
          };

          // Update businessInsights state for immediate display
          setBusinessInsights(prevState => ({
            ...prevState,
            lowStockItems: personalizedInsights.lowStockAlerts || [],
            reorderSuggestions: personalizedInsights.proactiveReorderSuggestions || [],
            alerts: crossSheetInsights.alerts || [],
            personalizedInsights: personalizedInsights,
            comprehensiveInsights
          }));

          return;
        } else {
          // Business insights cache expired
        }
      } else {
        // No cached business insights found
      }

      // If no cache or cache expired, set default empty insights
      // Keep default empty insights

    } catch (err) {
      // Failed to load business insights from cache
      // Keep default empty insights
    }
  };

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Check for cached dashboard data
      const cachedDashboardData = localStorage.getItem('dashboardMetricsData');
      const cachedDashboardTimestamp = localStorage.getItem('dashboardMetricsTimestamp');

      if (cachedDashboardData && cachedDashboardTimestamp) {
        const cacheAge = Date.now() - parseInt(cachedDashboardTimestamp);
        const cacheValidMinutes = 5; // Cache valid for 5 minutes for dashboard data

        if (cacheAge < (cacheValidMinutes * 60 * 1000)) {
          console.log('📊 Dashboard: Using cached metrics data');
          const cachedMetrics = JSON.parse(cachedDashboardData);
          setMetrics(cachedMetrics);
          setLoading(false);
          return;
        } else {
          console.log('📊 Dashboard: Cached metrics data expired, fetching fresh data');
        }
      }

      // Fetch pre-calculated metrics from new dashboard endpoint
      console.log('📊 Dashboard: Fetching fresh metrics from backend');
      const dashboardResponse = await backendApi.getDashboardMetrics();

      if (dashboardResponse && dashboardResponse.success) {
        const metricsData = dashboardResponse.metrics;
        console.log('📊 Dashboard: Received metrics:', metricsData);

        // Cache the metrics data
        localStorage.setItem('dashboardMetricsData', JSON.stringify(metricsData));
        localStorage.setItem('dashboardMetricsTimestamp', Date.now().toString());

        setMetrics(metricsData);
      } else {
        throw new Error('Invalid response format from dashboard API');
      }

    } catch (err) {
      setError('Failed to load dashboard data. Please check your connection.');
      console.error('Dashboard data loading error:', err);
    } finally {
      setLoading(false);
    }
  };
  const MetricCard = ({
    title,
    value,
    icon: Icon,
    trend,
    trendValue,
    subtitle,
    gradient,
  }) => {
    // Handle edge cases for trend value
    const displayTrendValue = trendValue && trendValue !== '0.0' && trendValue !== '0' ? parseFloat(trendValue).toFixed(1) : '0.0';
    const showTrend = trend && trendValue !== undefined && trendValue !== null && trendValue !== 0;

    return (
      <Card sx={{
        border: '1px solid rgba(99, 102, 241, 0.18)',
        boxShadow: '0 8px 22px rgba(15, 23, 42, 0.08)',
        transition: 'all 0.28s ease',
        borderRadius: 3,
        background: 'linear-gradient(145deg, rgba(255,255,255,0.95) 0%, rgba(248,250,252,0.92) 100%)',
        '&:hover': {
          boxShadow: '0 16px 30px rgba(15, 23, 42, 0.13)',
          borderColor: 'rgba(99, 102, 241, 0.35)',
          transform: 'translateY(-2px)',
        },
        position: 'relative',
        overflow: 'hidden',
      }}>
        <Box
          sx={{
            position: 'absolute',
            top: -42,
            right: -38,
            width: 112,
            height: 112,
            borderRadius: '50%',
            background: 'rgba(99, 102, 241, 0.08)',
          }}
        />
        <CardContent sx={{ p: 3 }}>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
            <Avatar sx={{
              background: gradient,
              width: 48,
              height: 48,
            }}>
              <Icon sx={{ color: 'white', fontSize: 20 }} />
            </Avatar>
            {showTrend && (
              <Chip
                icon={trend === "up" ? <ArrowUpwardIcon /> : <ArrowDownwardIcon />}
                label={`${displayTrendValue}%`}
                size="small"
                sx={{
                  bgcolor: trend === "up" ? 'success.main' : 'error.main',
                  color: 'white',
                  fontWeight: 600,
                  fontSize: '0.75rem',
                  height: 24,
                  '& .MuiChip-icon': { fontSize: 14 },
                }}
              />
            )}
          </Box>
          <Box>
            <Typography variant="h4" sx={{
              fontWeight: 700,
              fontSize: { xs: '1.55rem', md: '1.75rem' },
              mb: 1,
              lineHeight: 1.2,
            }}>
              ₹{value.toLocaleString('en-IN')}
            </Typography>
            <Typography variant="body1" sx={{
              fontWeight: 600,
              color: 'text.secondary',
              mb: 0.5,
            }}>
              {title}
            </Typography>
            {subtitle && (
              <Typography variant="body2" sx={{
                color: 'text.secondary',
                fontSize: '0.875rem',
              }}>
                {subtitle}
              </Typography>
            )}
          </Box>
        </CardContent>
      </Card>
    );
  };

  const QuickActionButton = ({ title, description, icon: Icon, onClick }) => (
    <Button
      variant="outlined"
      fullWidth
      onClick={onClick}
      sx={{
        justifyContent: 'flex-start',
        p: 2.1,
        border: '1px solid rgba(99, 102, 241, 0.16)',
        borderRadius: 2.5,
        backgroundColor: 'rgba(255,255,255,0.75)',
        boxShadow: '0 6px 18px rgba(15, 23, 42, 0.07)',
        transition: 'all 0.25s ease',
        '&:hover': {
          backgroundColor: 'rgba(255,255,255,0.95)',
          borderColor: 'rgba(99, 102, 241, 0.32)',
          boxShadow: '0 12px 24px rgba(15, 23, 42, 0.12)',
          transform: 'translateY(-1px)',
        },
        textTransform: 'none',
      }}
      startIcon={<Icon sx={{ fontSize: 18 }} />}
    >
      <Box sx={{ textAlign: 'left', flex: 1 }}>
        <Typography variant="body1" sx={{ fontWeight: 600, fontSize: '0.95rem' }}>
          {title}
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.8rem', mt: 0.25 }}>
          {description}
        </Typography>
      </Box>
    </Button>
  );

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: `
              radial-gradient(circle at 20% 80%, rgba(120, 119, 198, 0.1) 0%, transparent 50%),
              radial-gradient(circle at 80% 20%, rgba(255, 119, 198, 0.1) 0%, transparent 50%),
              radial-gradient(circle at 40% 40%, rgba(120, 219, 226, 0.1) 0%, transparent 50%)
            `,
            pointerEvents: 'none',
          }
        }}
      >
        <Box sx={{ position: 'relative', zIndex: 1, textAlign: 'center' }}>
          <Box
            sx={{
              width: 120,
              height: 120,
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mb: 4,
              mx: 'auto',
              boxShadow: '0 20px 40px rgba(0, 0, 0, 0.1)',
              animation: 'pulse 2s infinite',
            }}
          >
            <AssessmentIcon sx={{ fontSize: 60, color: 'white' }} />
          </Box>

          <Typography
            variant="h4"
            sx={{
              fontWeight: 800,
              mb: 2,
              background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            Loading Dashboard
          </Typography>

          <Typography variant="h6" sx={{ color: 'text.secondary', mb: 4, fontWeight: 500 }}>
            Fetching your business insights...
          </Typography>

          <Box sx={{ width: 300, mx: 'auto' }}>
            <LinearProgress
              sx={{
                height: 8,
                borderRadius: 4,
                background: 'rgba(0, 0, 0, 0.1)',
                '& .MuiLinearProgress-bar': {
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  borderRadius: 4,
                }
              }}
            />
          </Box>

          <Typography variant="body2" sx={{ color: 'text.secondary', mt: 2, opacity: 0.7 }}>
            Please wait while we prepare your data
          </Typography>
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: `
              radial-gradient(circle at 20% 80%, rgba(239, 68, 68, 0.1) 0%, transparent 50%),
              radial-gradient(circle at 80% 20%, rgba(239, 68, 68, 0.05) 0%, transparent 50%)
            `,
            pointerEvents: 'none',
          }
        }}
      >
        <Paper
          elevation={0}
          sx={{
            maxWidth: 500,
            width: '90%',
            p: 4,
            borderRadius: 4,
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(239, 68, 68, 0.2)',
            textAlign: 'center',
            position: 'relative',
            zIndex: 1,
          }}
        >
          <Avatar
            sx={{
              width: 80,
              height: 80,
              bgcolor: 'error.main',
              mx: 'auto',
              mb: 3,
              boxShadow: '0 16px 32px rgba(239, 68, 68, 0.2)',
            }}
          >
            <AssessmentIcon sx={{ fontSize: 40 }} />
          </Avatar>

          <Typography
            variant="h5"
            sx={{
              fontWeight: 800,
              mb: 2,
              color: 'error.main',
            }}
          >
            Unable to Load Dashboard
          </Typography>

          <Typography
            variant="body1"
            sx={{
              color: 'text.secondary',
              mb: 4,
              lineHeight: 1.6,
            }}
          >
            {error}
          </Typography>

          <Button
            variant="contained"
            onClick={loadDashboardData}
            sx={{
              borderRadius: 3,
              px: 4,
              py: 1.5,
              fontWeight: 600,
              background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
              boxShadow: '0 8px 24px rgba(239, 68, 68, 0.3)',
              '&:hover': {
                background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
                boxShadow: '0 12px 32px rgba(239, 68, 68, 0.4)',
              }
            }}
          >
            Try Again
          </Button>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* Header Section */}
      <Box sx={{ maxWidth: 1480, mx: 'auto', px: { xs: 2, md: 3 }, pt: 3, pb: 1.5 }}>
        <Card
          sx={{
            borderRadius: 4,
            overflow: 'hidden',
            border: '1px solid rgba(99, 102, 241, 0.2)',
            background: 'linear-gradient(120deg, #0f172a 0%, #312e81 50%, #4338ca 100%)',
            position: 'relative',
            boxShadow: '0 18px 38px rgba(15, 23, 42, 0.22)'
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              width: 240,
              height: 240,
              right: -90,
              top: -110,
              borderRadius: '50%',
              background: 'radial-gradient(circle, rgba(255,255,255,0.22) 0%, rgba(255,255,255,0) 70%)'
            }}
          />
          <CardContent sx={{ p: { xs: 2.2, md: 3 }, position: 'relative', zIndex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: { xs: 'flex-start', md: 'center' }, justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
              <Box>
                <Typography variant="h3" sx={{
                  fontWeight: 800,
                  fontSize: { xs: '1.85rem', md: '2.35rem' },
                  mb: 0.8,
                  letterSpacing: '-0.02em',
                  color: '#fff'
                }}>
                  Business Dashboard
                </Typography>
                <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.86)', fontSize: { xs: '1rem', md: '1.08rem' }, maxWidth: 620 }}>
                  Monitor your business performance and key metrics in real-time
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: { xs: 'flex-start', md: 'flex-end' }, gap: 1.2 }}>
                <Box sx={{ textAlign: { xs: 'left', md: 'right' } }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: { xs: 'flex-start', md: 'flex-end' }, gap: 1, mb: 0.5 }}>
                    <Box sx={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      backgroundColor: (() => {
                        const savedTimestamp = localStorage.getItem('businessAnalyticsTimestamp');
                        if (savedTimestamp) {
                          const hoursSinceUpdate = (Date.now() - parseInt(savedTimestamp)) / (1000 * 60 * 60);
                          if (hoursSinceUpdate < 2) return '#22c55e';
                          if (hoursSinceUpdate < 12) return '#f59e0b';
                          return '#ef4444';
                        }
                        return '#94a3b8';
                      })()
                    }} />
                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.88)', fontWeight: 600 }}>
                      {(() => {
                        const savedTimestamp = localStorage.getItem('businessAnalyticsTimestamp');
                        if (savedTimestamp) {
                          const hoursSinceUpdate = (Date.now() - parseInt(savedTimestamp)) / (1000 * 60 * 60);
                          if (hoursSinceUpdate < 2) return 'Fresh Data';
                          if (hoursSinceUpdate < 12) return 'Recent Data';
                          return 'Outdated Data';
                        }
                        return 'No Data';
                      })()}
                    </Typography>
                  </Box>
                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.72)', display: 'block' }}>
                    Last updated: {(() => {
                      const savedTimestamp = localStorage.getItem('businessAnalyticsTimestamp');
                      if (savedTimestamp) {
                        return new Date(parseInt(savedTimestamp)).toLocaleString('en-IN', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        });
                      }
                      return 'Never';
                    })()}
                  </Typography>
                </Box>

                <Button
                  variant="contained"
                  startIcon={<VisibilityIcon />}
                  onClick={loadDashboardData}
                  sx={{
                    borderRadius: 99,
                    textTransform: 'none',
                    fontWeight: 700,
                    px: 2.2,
                    py: 0.9,
                    background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
                    boxShadow: '0 10px 22px rgba(79, 70, 229, 0.35)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #4f46e5 0%, #4338ca 100%)',
                    }
                  }}
                >
                  Refresh Data
                </Button>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Box>

      {/* Main Content */}
      <Box sx={{ maxWidth: 1480, mx: 'auto', px: { xs: 2, md: 3 }, py: { xs: 3, md: 4.5 } }}>
        {/* Key Metrics Cards */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, 1fr)',
              lg: 'repeat(4, 1fr)'
            },
            gap: 2.25,
          }}>
            <MetricCard
              title="Total Sales"
              value={metrics.totalSales}
              icon={TrendingUpIcon}
              trend={metrics.salesGrowth >= 0 ? "up" : "down"}
              trendValue={Math.abs(metrics.salesGrowth).toFixed(1)}
              subtitle="All time"
              gradient="linear-gradient(135deg, #f093fb 0%, #f5576c 100%)"
            />
            <MetricCard
              title="Total Purchases"
              value={metrics.totalPurchases}
              icon={ShoppingCartIcon}
              trend={metrics.purchasesGrowth >= 0 ? "up" : "down"}
              trendValue={Math.abs(metrics.purchasesGrowth).toFixed(1)}
              subtitle="All time"
              gradient="linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)"
            />
            <MetricCard
              title="Inventory Value"
              value={metrics.inventoryValue}
              icon={InventoryIcon}
              trend={metrics.inventoryGrowth >= 0 ? "up" : "down"}
              trendValue={Math.abs(metrics.inventoryGrowth).toFixed(1)}
              subtitle="Current stock"
              gradient="linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)"
            />
            <MetricCard
              title="Net Profit"
              value={metrics.totalProfit}
              icon={AccountBalanceIcon}
              trend={metrics.profitGrowth >= 0 ? "up" : "down"}
              trendValue={Math.abs(metrics.profitGrowth).toFixed(1)}
              subtitle="All time"
              gradient="linear-gradient(135deg, #fa709a 0%, #fee140 100%)"
            />
          </Box>
        </Box>

        {/* Quick Actions Card */}
        <Card sx={{
          border: '1px solid rgba(99, 102, 241, 0.16)',
          boxShadow: '0 10px 24px rgba(15, 23, 42, 0.08)',
          borderRadius: 3,
          mb: 4,
          background: 'linear-gradient(145deg, rgba(255,255,255,0.94) 0%, rgba(248,250,252,0.92) 100%)'
        }}>
          <CardContent sx={{ p: 4 }}>
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 3, display: 'flex', alignItems: 'center', gap: 1.2 }}>
              <SpeedIcon sx={{ color: 'primary.main' }} />
              Quick Actions
            </Typography>

            <Box sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
              gap: 2
            }}>
              <QuickActionButton
                title="Add Sale"
                description="Record a new sale transaction"
                icon={ReceiptIcon}
                onClick={() => window.location.href = '/sales'}
              />
              <QuickActionButton
                title="Add Purchase"
                description="Record a new purchase order"
                icon={ShoppingCartIcon}
                onClick={() => window.location.href = '/purchases'}
              />
              <QuickActionButton
                title="Generate Invoice"
                description="Create professional invoices"
                icon={AnalyticsIcon}
                onClick={() => window.location.href = '/invoices'}
              />
              <QuickActionButton
                title="Manage Customers"
                description="View and manage customer data"
                icon={PeopleIcon}
                onClick={() => window.location.href = '/customers'}
              />
            </Box>
          </CardContent>
        </Card>

        {/* Business Insights Card - Full Width */}
        <Card sx={{
          border: '1px solid rgba(99, 102, 241, 0.16)',
          boxShadow: '0 10px 24px rgba(15, 23, 42, 0.08)',
          borderRadius: 3,
          position: 'relative',
          overflow: 'hidden',
          background: 'linear-gradient(145deg, rgba(255,255,255,0.96) 0%, rgba(248,250,252,0.94) 100%)'
        }}>
          <CardContent sx={{ p: 4 }}>
            <Box sx={{ display: 'flex', alignItems: { xs: 'flex-start', md: 'center' }, justifyContent: 'space-between', mb: 4, gap: 2, flexWrap: 'wrap' }}>
              <Typography variant="h5" sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 2 }}>
                <AssessmentIcon sx={{ fontSize: 28, color: 'primary.main' }} />
                Business Insights
              </Typography>
              <Button
                component={Link}
                to="/business-intelligence"
                variant="contained"
                startIcon={<VisibilityIcon />}
                sx={{
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 600,
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  boxShadow: '0 4px 16px rgba(102, 126, 234, 0.3)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%)',
                    boxShadow: '0 6px 24px rgba(102, 126, 234, 0.4)',
                    transform: 'translateY(-1px)',
                  }
                }}
              >
                View Full Insights
              </Button>
            </Box>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' }, gap: 3, mb: 4 }}>
              {/* Business Health Score */}
              <Box sx={{ textAlign: 'center', p: 3, background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.1) 0%, rgba(139, 92, 246, 0.1) 100%)', borderRadius: 3, border: '1px solid rgba(99, 102, 241, 0.2)' }}>
                <Typography variant="h3" sx={{
                  fontWeight: 800,
                  color: 'primary.main',
                  mb: 1,
                  fontSize: '2.5rem'
                }}>
                  {(() => {
                    // Get business health score from the correct data structure
                    // Access the same data structure as Business Intelligence page
                    const cachedAnalyticsData = localStorage.getItem('businessAnalyticsData');
                    let healthScore = 75; // Default to 75 if no score available

                    if (cachedAnalyticsData) {
                      try {
                        const analytics = JSON.parse(cachedAnalyticsData);
                        const overviewData = analytics.overview?.overview || analytics.overview || {};
                        const crossSheetInsights = overviewData.crossSheetInsights || {};

                        // Try the correct data paths (same as Business Intelligence page)
                        healthScore = crossSheetInsights.keyMetrics?.businessHealthScore ||
                                    crossSheetInsights.overallHealthScore ||
                                    overviewData.comprehensiveInsights?.businessHealth?.score ||
                                    crossSheetInsights.keyMetrics?.overallHealthScore ||
                                    75; // Default to 75 if no score available

                        // Health score extracted successfully
                      } catch (error) {
                        console.warn('📊 Dashboard: Error parsing cached analytics data:', error);
                      }
                    }

                    return healthScore;
                  })()}
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
                  Business Health Score
                </Typography>
                <Chip
                  label={(() => {
                    // Get business health status from the correct data structure
                    const cachedAnalyticsData = localStorage.getItem('businessAnalyticsData');
                    let healthStatus = 'GOOD'; // Default to GOOD if no status available

                    if (cachedAnalyticsData) {
                      try {
                        const analytics = JSON.parse(cachedAnalyticsData);
                        const overviewData = analytics.overview?.overview || analytics.overview || {};
                        const crossSheetInsights = overviewData.crossSheetInsights || {};

                        // Try the correct data paths (same as Business Intelligence page)
                        healthStatus = crossSheetInsights.overallHealth ||
                                     overviewData.comprehensiveInsights?.businessHealth?.status ||
                                     crossSheetInsights.keyMetrics?.overallHealth ||
                                     'GOOD'; // Default to GOOD if no status available

                        // Health status extracted successfully
                      } catch (error) {
                        console.warn('📊 Dashboard: Error parsing cached analytics data for status:', error);
                      }
                    }

                    return healthStatus;
                  })()}
                  color={(() => {
                    const cachedAnalyticsData = localStorage.getItem('businessAnalyticsData');
                    let healthStatus = 'GOOD'; // Default to GOOD if no status available

                    if (cachedAnalyticsData) {
                      try {
                        const analytics = JSON.parse(cachedAnalyticsData);
                        const overviewData = analytics.overview?.overview || analytics.overview || {};
                        const crossSheetInsights = overviewData.crossSheetInsights || {};

                        // Try the correct data paths (same as Business Intelligence page)
                        healthStatus = crossSheetInsights.overallHealth ||
                                     overviewData.comprehensiveInsights?.businessHealth?.status ||
                                     crossSheetInsights.keyMetrics?.overallHealth ||
                                     'GOOD'; // Default to GOOD if no status available
                      } catch (error) {
                        // Error parsing cached analytics data for color
                      }
                    }

                    switch (healthStatus) {
                      case 'EXCELLENT': return 'success';
                      case 'GOOD': return 'primary';
                      case 'FAIR': return 'warning';
                      case 'CONCERNING': return 'warning';
                      case 'CRITICAL': return 'error';
                      default: return 'primary'; // Default to primary for GOOD status
                    }
                  })()}
                  size="small"
                />
              </Box>

              {/* Low Stock Items */}
              <Box sx={{ textAlign: 'center', p: 3, background: 'linear-gradient(135deg, rgba(255, 152, 0, 0.1) 0%, rgba(255, 193, 7, 0.1) 100%)', borderRadius: 3, border: '1px solid rgba(255, 152, 0, 0.2)' }}>
                <Typography variant="h3" sx={{
                  fontWeight: 800,
                  color: 'warning.main',
                  mb: 1,
                  fontSize: '2.5rem'
                }}>
                  {businessInsights.lowStockItems?.length || 0}
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
                  Low Stock Items
                </Typography>
                <Typography variant="caption" sx={{ color: 'warning.dark' }}>
                  Items running low
                </Typography>
              </Box>

              {/* Top Customer Count */}
              <Box sx={{ textAlign: 'center', p: 3, background: 'linear-gradient(135deg, rgba(76, 175, 80, 0.1) 0%, rgba(139, 195, 74, 0.1) 100%)', borderRadius: 3, border: '1px solid rgba(76, 175, 80, 0.2)' }}>
                <Typography variant="h3" sx={{
                  fontWeight: 800,
                  color: 'success.main',
                  mb: 1,
                  fontSize: '2.5rem'
                }}>
                  {businessInsights.personalizedInsights?.topCustomers?.length || 0}
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
                  Top Customers
                </Typography>
                <Typography variant="caption" sx={{ color: 'success.dark' }}>
                  High-value customers
                </Typography>
              </Box>

              {/* Trending Products */}
              <Box sx={{ textAlign: 'center', p: 3, background: 'linear-gradient(135deg, rgba(33, 150, 243, 0.1) 0%, rgba(3, 169, 244, 0.1) 100%)', borderRadius: 3, border: '1px solid rgba(33, 150, 243, 0.2)' }}>
                <Typography variant="h3" sx={{
                  fontWeight: 800,
                  color: 'info.main',
                  mb: 1,
                  fontSize: '2.5rem'
                }}>
                  {businessInsights.personalizedInsights?.trendingProducts?.length || 0}
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
                  Trending Products
                </Typography>
                <Typography variant="caption" sx={{ color: 'info.dark' }}>
                  Popular items
                </Typography>
              </Box>
            </Box>

            {/* Financial Overview from Stored Data */}
            {(() => {
              const crossSheetInsights = businessInsights.comprehensiveInsights?.crossSheetInsights || {};
              const hasFinancialData = crossSheetInsights.keyMetrics?.totalRevenue ||
                                     crossSheetInsights.keyMetrics?.totalExpenses ||
                                     crossSheetInsights.keyMetrics?.netProfit;

              if (hasFinancialData) {
                return (
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 3, mb: 4 }}>
                    <Box sx={{ p: 3, bgcolor: 'rgba(76, 175, 80, 0.05)', borderRadius: 3, border: '1px solid rgba(76, 175, 80, 0.2)' }}>
                      <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: 'success.main', display: 'flex', alignItems: 'center', gap: 1 }}>
                        <TrendingUpIcon sx={{ fontSize: 20 }} />
                        Total Revenue
                      </Typography>
                      <Typography variant="h4" sx={{ fontWeight: 800, color: 'success.main', mb: 1 }}>
                        ₹{(crossSheetInsights.keyMetrics?.totalRevenue || crossSheetInsights.keyMetrics?.totalSales || 0).toLocaleString('en-IN')}
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        From sales data
                      </Typography>
                    </Box>

                    <Box sx={{ p: 3, bgcolor: 'rgba(244, 67, 54, 0.05)', borderRadius: 3, border: '1px solid rgba(244, 67, 54, 0.2)' }}>
                      <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: 'error.main', display: 'flex', alignItems: 'center', gap: 1 }}>
                        <AccountBalanceIcon sx={{ fontSize: 20 }} />
                        Total Expenses
                      </Typography>
                      <Typography variant="h4" sx={{ fontWeight: 800, color: 'error.main', mb: 1 }}>
                        ₹{(crossSheetInsights.keyMetrics?.totalExpenses || crossSheetInsights.keyMetrics?.totalPurchases || 0).toLocaleString('en-IN')}
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        From purchase data
                      </Typography>
                    </Box>

                    <Box sx={{ p: 3, bgcolor: 'rgba(33, 150, 243, 0.05)', borderRadius: 3, border: '1px solid rgba(33, 150, 243, 0.2)' }}>
                      <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: 'info.main', display: 'flex', alignItems: 'center', gap: 1 }}>
                        <AccountBalanceIcon sx={{ fontSize: 20 }} />
                        Net Profit
                      </Typography>
                      <Typography variant="h4" sx={{ fontWeight: 800, color: 'info.main', mb: 1 }}>
                        ₹{(crossSheetInsights.keyMetrics?.netProfit || crossSheetInsights.keyMetrics?.totalProfit || 0).toLocaleString('en-IN')}
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        Revenue - Expenses
                      </Typography>
                    </Box>
                  </Box>
                );
              }
              return null;
            })()}

            {/* Detailed Insights from Stored Data */}
            {businessInsights.personalizedInsights?.trendingProducts?.length > 0 && (
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 4, mb: 4 }}>
                {/* Top Trending Products */}
                <Box sx={{ p: 3, bgcolor: 'rgba(76, 175, 80, 0.05)', borderRadius: 3, border: '1px solid rgba(76, 175, 80, 0.2)' }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, color: 'success.main', display: 'flex', alignItems: 'center', gap: 1 }}>
                    <TrendingUpIcon sx={{ fontSize: 20 }} />
                    Top Trending Products
                  </Typography>
                  {businessInsights.personalizedInsights.trendingProducts.slice(0, 3).map((product, index) => (
                    <Box key={index} sx={{ mb: 2, p: 2, bgcolor: 'background.paper', borderRadius: 2, border: '1px solid rgba(0, 0, 0, 0.08)' }}>
                      <Typography variant="body1" sx={{ fontWeight: 600, color: 'text.primary' }}>
                        {product.productName}
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
                        {product.totalSold} sold • ₹{product.totalRevenue} revenue
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Chip
                          label={product.trend}
                          size="small"
                          sx={{
                            fontSize: '0.7rem',
                            height: 20,
                            bgcolor: product.trend === 'HIGH' ? 'success.main' : product.trend === 'MEDIUM' ? 'warning.main' : 'info.main',
                            color: 'white'
                          }}
                        />
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                          {product.uniqueCustomers} customers
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </Box>

                {/* Top Customers */}
                <Box sx={{ p: 3, bgcolor: 'rgba(33, 150, 243, 0.05)', borderRadius: 3, border: '1px solid rgba(33, 150, 243, 0.2)' }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, color: 'info.main', display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PeopleIcon sx={{ fontSize: 20 }} />
                    Top Customers
                  </Typography>
                  {businessInsights.personalizedInsights.topCustomers.slice(0, 3).map((customer, index) => (
                    <Box key={index} sx={{ mb: 2, p: 2, bgcolor: 'background.paper', borderRadius: 2, border: '1px solid rgba(0, 0, 0, 0.08)' }}>
                      <Typography variant="body1" sx={{ fontWeight: 600, color: 'text.primary' }}>
                        {customer.customerName}
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
                        ₹{customer.totalSpent} spent • {customer.purchaseCount} orders
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Chip
                          label={customer.loyalty}
                          size="small"
                          sx={{
                            fontSize: '0.7rem',
                            height: 20,
                            bgcolor: customer.loyalty === 'HIGH' ? 'success.main' : customer.loyalty === 'MEDIUM' ? 'warning.main' : 'info.main',
                            color: 'white'
                          }}
                        />
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                          ₹{customer.avgOrderValue}/order
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}

            {/* Low Stock Alerts */}
            {businessInsights.lowStockItems?.length > 0 && (
              <Box sx={{ p: 3, bgcolor: 'rgba(244, 67, 54, 0.05)', borderRadius: 3, border: '1px solid rgba(244, 67, 54, 0.2)', mb: 4 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, color: 'error.main', display: 'flex', alignItems: 'center', gap: 1 }}>
                  <InventoryIcon sx={{ fontSize: 20 }} />
                  Low Stock Alerts
                </Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' }, gap: 2 }}>
                  {businessInsights.lowStockItems.slice(0, 6).map((item, index) => (
                    <Box key={index} sx={{ p: 2, bgcolor: 'background.paper', borderRadius: 2, border: '1px solid rgba(244, 67, 54, 0.2)' }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: 'error.main' }}>
                        {item.productName || item.itemName || 'Unknown Product'}
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'error.dark', display: 'block' }}>
                        {item.currentStock || item.quantity || 0} units remaining
                      </Typography>
                      <Chip
                        label="LOW STOCK"
                        size="small"
                        color="error"
                        sx={{ mt: 1, fontSize: '0.7rem', height: 18 }}
                      />
                    </Box>
                  ))}
                </Box>
                {businessInsights.lowStockItems.length > 6 && (
                  <Typography variant="caption" sx={{ color: 'text.secondary', mt: 2, display: 'block', textAlign: 'center' }}>
                    ...and {businessInsights.lowStockItems.length - 6} more items need restocking
                  </Typography>
                )}
              </Box>
            )}

            <Box sx={{ textAlign: 'center', p: 3, background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.05) 0%, rgba(139, 92, 246, 0.05) 100%)', borderRadius: 2, border: '1px solid rgba(99, 102, 241, 0.1)' }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: 'primary.main' }}>
                🚀 Ready to unlock deeper insights?
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
                Get comprehensive AI-powered analysis, strategic recommendations, and advanced analytics to optimize your business performance.
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                Click "View Full Insights" to access the complete Business Intelligence Hub
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </Box>

      {/* Modern Footer */}
      <Box
        sx={{
          position: 'relative',
          zIndex: 1,
          py: 3.5,
          px: { xs: 2, md: 3 },
          textAlign: 'center',
          borderTop: '1px solid rgba(99, 102, 241, 0.12)',
          background: 'rgba(255, 255, 255, 0.82)',
          backdropFilter: 'blur(20px)',
        }}
      >
        <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
              fontWeight: 500,
              fontSize: '0.95rem',
              mb: 2,
            }}
          >
            Last updated: {new Date().toLocaleString('en-IN')}
          </Typography>

          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              mb: 1,
            }}
          >
            Dhandhapaani
          </Typography>

          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
              fontSize: '0.85rem',
              opacity: 0.8,
            }}
          >
            Powered by Maruti Agritech
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}

export default Dashboard;


